import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { CommonError } from 'src/api/base'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { errorMessage } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { getStatusColor, getStatusLabel, STATUS_OPTIONS } from '../portfolioUtils'

interface FilterValues {
  readonly semId: string
  readonly sectionId: string
  readonly semester: string
  readonly status: string
  readonly searchStudent: string
  readonly searchMentor: string
}

interface SelectOption {
  readonly value: string
  readonly label: string
}

export default function AdministratorPortfolioWorksPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('portfolio:works')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const t = useTranslatable()
  const activeSemester = useActiveSemester()
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '10',
  })

  const page = searchParams.get('page') ?? '1'
  const perPage = searchParams.get('perPage') ?? '10'
  const semId = searchParams.get('semId') ?? activeSemester?.id ?? ''
  const sectionId = searchParams.get('sectionId') ?? ''
  const semester = searchParams.get('semester') ?? ''
  const status = searchParams.get('status') ?? ''
  const searchStudent = searchParams.get('searchStudent') ?? ''
  const searchMentor = searchParams.get('searchMentor') ?? ''

  // Fetch sections for dropdown
  const { data: sections } = useApi({
    endpoint: Api.getAdministratorPortfolioListsSections,
    params: React.useMemo(
      () => ({
        headers,
      }),
      [headers]
    ),
  })

  // Fetch student semesters for dropdown
  const { data: studentSemesters } = useApi({
    endpoint: Api.getAdministratorPortfolioListsStudentSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  // Fetch portfolio semesters for dropdown
  const { data: portfolioSemesters } = useApi({
    endpoint: Api.getAdministratorPortfolioListsPortfolioSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  // Fetch works
  const { data: works, error } = useApi({
    endpoint: Api.getAdministratorPortfolioWorks,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page: Number(page),
          perPage: Number(perPage),
          semId,
          sectionId,
          semester,
          status,
          searchStudent,
          searchMentor,
        },
      }),
      [headers, page, perPage, semId, sectionId, semester, status, searchStudent, searchMentor]
    ),
  })

  const sectionOptions = React.useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('common:all') },
      ...(sections?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? []),
    ],
    [sections, t]
  )

  const studentSemesterOptions = React.useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('common:all') },
      ...(studentSemesters?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? []),
    ],
    [studentSemesters, t]
  )

  const semIdOptions = React.useMemo<SelectOption[]>(
    () => portfolioSemesters?.items?.map((s) => ({ value: s.id.toString(), label: s.text })) ?? [],
    [portfolioSemesters]
  )

  const statusOptions = React.useMemo<SelectOption[]>(
    () => STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.label) })),
    [t]
  )

  const initialValues = React.useMemo<FilterValues>(
    () => ({
      semId,
      sectionId,
      semester,
      status,
      searchStudent,
      searchMentor,
    }),
    [semId, sectionId, semester, status, searchStudent, searchMentor]
  )

  const handleSearch = React.useCallback(
    (values: FilterValues) => {
      const newParams = new URLSearchParams()
      newParams.set('page', '1')
      newParams.set('perPage', perPage)
      if (values.semId) newParams.set('semId', values.semId)
      if (values.sectionId) newParams.set('sectionId', values.sectionId)
      if (values.semester) newParams.set('semester', values.semester)
      if (values.status) newParams.set('status', values.status)
      if (values.searchStudent) newParams.set('searchStudent', values.searchStudent)
      if (values.searchMentor) newParams.set('searchMentor', values.searchMentor)
      setSearchParams(newParams)
    },
    [perPage, setSearchParams]
  )

  const handleReset = React.useCallback(() => {
    const newParams = new URLSearchParams()
    newParams.set('page', '1')
    newParams.set('perPage', perPage)
    if (activeSemester?.id) newParams.set('semId', activeSemester.id)
    setSearchParams(newParams)
  }, [perPage, activeSemester?.id, setSearchParams])

  const breadcrumbsItems = [
    {
      page: `${t('portfolio:portfolio')} - ${t('portfolio:works')}`,
      path: `/${locale}/administrator/portfolio/works`,
    },
  ]

  const hasData = works != null && works.data.length > 0

  return (
    <Section title={t('portfolio:works')} breadcrubms={breadcrumbsItems} icon={<PortfolioIcon />}>
      <Form initialValues={initialValues} enableReinitialize onSubmit={handleSearch} classNames={{ form: 'w-full' }}>
        <FilterForm
          semIdOptions={semIdOptions}
          sectionOptions={sectionOptions}
          studentSemesterOptions={studentSemesterOptions}
          statusOptions={statusOptions}
          onReset={handleReset}
        />
      </Form>

      {error != null ? (
        <NoContent
          header={t('error:an_error_occurred')}
          image={Warning}
          subHeader={errorMessage(error as unknown as CommonError)}
        />
      ) : !hasData ? (
        <NoContent
          header={t('error:records_not_found')}
          image={Warning}
          subHeader={t(['ნამუშევრები არ მოიძებნა', 'No works found'])}
        />
      ) : (
        <>
          <PaginationTop pagination={works.meta.pagination} />
          <Table.Table className="mt-7 w-full">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('common:unique')}</Table.Th>
                <Table.Th>{t('portfolio:mentor')}</Table.Th>
                <Table.Th>{t('student:student')}</Table.Th>
                <Table.Th>{t('portfolio:section')}</Table.Th>
                <Table.Th>{t('common:semester')}</Table.Th>
                <Table.Th>{t('common:status')}</Table.Th>
                <Table.Th>{t('common:created_at')}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {works.data.map((work) => (
                <Table.Tr key={work.id}>
                  <Table.Td>{work.id}</Table.Td>
                  <Table.Td>{`${work.mentor?.name} (${work.mentor?.persNum})`}</Table.Td>
                  <Table.Td>{`${work.student?.name} (${work.student?.persNum})`}</Table.Td>
                  <Table.Td>{work.section?.name}</Table.Td>
                  <Table.Td>{work.semester}</Table.Td>
                  <Table.Td>
                    <span className={getStatusColor(work.status)}>{getStatusLabel(t, work.status.value)}</span>
                  </Table.Td>
                  <Table.Td>{work.createdAt}</Table.Td>
                  <Table.Td>
                    <Link to={`/${locale}/administrator/portfolio/works/edit/${work.id}`}>
                      <FontAwesomeIcon icon={ProRegularSvgIcons.faPencil} color="gray" />
                    </Link>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table.Table>
          <PaginationBottom pagination={works.meta.pagination} />
        </>
      )}
    </Section>
  )
}

interface FilterFormProps {
  readonly semIdOptions: SelectOption[]
  readonly sectionOptions: SelectOption[]
  readonly studentSemesterOptions: SelectOption[]
  readonly statusOptions: SelectOption[]
  readonly onReset: () => void
}

function FilterForm({
  semIdOptions,
  sectionOptions,
  studentSemesterOptions,
  statusOptions,
  onReset,
}: FilterFormProps): JSX.Element {
  const t = useTranslatable()
  const formik = useFormikContext<FilterValues>()

  const handleReset = React.useCallback(() => {
    formik.resetForm()
    onReset()
  }, [formik, onReset])

  return (
    <div className="mb-6 rounded-card bg-card p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <SelectInput name="sectionId" label={t('portfolio:section')} options={sectionOptions} />
        <SelectInput name="status" label={t('common:status')} options={statusOptions} />
        <SelectInput name="semester" label={t('student:student_semester')} options={studentSemesterOptions} />
        <SelectInput name="semId" label={t('common:semester')} options={semIdOptions} />
        <TextInput name="searchStudent" type="text" label={t('student:student')} placeholder={t('common:search')} />
        <TextInput name="searchMentor" type="text" label={t('portfolio:mentor')} placeholder={t('common:search')} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="yellow" onClick={handleReset}>
          {t('common:cancel')}
        </Button>
        <Button type="submit" variant="red">
          {t('common:search')}
        </Button>
      </div>
    </div>
  )
}
